/**
 * Yakıt aktarımı yardımcıları (04.11.2026, #547 Petrol Ofisi entegrasyonu). Saf fonksiyonlar (axios içe aktarmaz; jest'te
 * doğrudan sınanır): kaynak / hata durumu rozet renkleri, Petrol Ofisi'nden çekim tarih varsayılanı, sonuç mesajları.
 */

/** Yakıt fişinin kaynağı (sunucudaki YakitKaynak ile aynı). */
export const YAKIT_KAYNAK = { ELLE: 1, EXCEL: 2, PETROL_OFISI: 3 };

/** Aktarım hatası durumu (sunucudaki YakitAktarimHataDurumu ile aynı). */
export const AKTARIM_HATA_DURUM = { BEKLIYOR: 1, DUZELTILDI: 2, YOK_SAYILDI: 3, PO_SILINDI: 4 };

/** Kaynak rozeti sınıfı: Elle gri, Excel mavi, Petrol Ofisi yeşil. Kaynak gelmezse addan çıkarılır. */
export const kaynakRozeti = (kaynak: number | null | undefined, kaynakAdi?: string | null): string => {
    let k = kaynak;

    if (k == null) {
        const ad = (kaynakAdi ?? '').toLocaleLowerCase('tr-TR');
        k = ad.includes('petrol') ? YAKIT_KAYNAK.PETROL_OFISI : ad.includes('excel') ? YAKIT_KAYNAK.EXCEL : YAKIT_KAYNAK.ELLE;
    }

    switch (k) {
        case YAKIT_KAYNAK.EXCEL: return 'badge badge-light-primary';
        case YAKIT_KAYNAK.PETROL_OFISI: return 'badge badge-light-success';
        default: return 'badge badge-light';
    }
}

/** Hata durumu rozeti: Bekliyor turuncu, Düzeltildi yeşil, Yok sayıldı gri, Petrol Ofisi'nde silindi kırmızı. */
export const hataDurumRozeti = (durum: number): string => {
    switch (durum) {
        case AKTARIM_HATA_DURUM.BEKLIYOR: return 'badge badge-light-warning';
        case AKTARIM_HATA_DURUM.DUZELTILDI: return 'badge badge-light-success';
        case AKTARIM_HATA_DURUM.PO_SILINDI: return 'badge badge-light-danger';
        default: return 'badge badge-light';
    }
}

const gunYaz = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** Petrol Ofisi'nden çek penceresinin varsayılan aralığı: bugün − 6 gün → bugün (7 gün, yerel saat). */
export const varsayilanCekimAraligi = (bugun: Date = new Date()): { baslangic: string, bitis: string } => {
    const bas = new Date(bugun.getFullYear(), bugun.getMonth(), bugun.getDate() - 6);
    return { baslangic: gunYaz(bas), bitis: gunYaz(bugun) };
}

/** Çekim aralığı denetimi: ikisi de dolu ve başlangıç bitişten sonra değil. Hata yoksa undefined. */
export const cekimAraligiHatasi = (baslangic: string | undefined, bitis: string | undefined): string | undefined => {
    if (!baslangic || !bitis)
        return 'Başlangıç ve bitiş tarihini seçin.';
    if (baslangic > bitis)
        return 'Başlangıç tarihi bitiş tarihinden sonra olamaz.';
    return undefined;
}

export interface HataIslemSonucu {
    duzeltilen: number
    kalan: number
    hatalar: string[]
}

/** Yeniden işle / düzelt sonucu özeti: "N düzeltildi, M hâlâ hatalı". */
export const islemSonucuOzeti = (s: HataIslemSonucu): string => {
    if (s.kalan == 0)
        return `${s.duzeltilen} kayıt düzeltildi.`;
    return `${s.duzeltilen} düzeltildi, ${s.kalan} hâlâ hatalı.`;
}

/** Excel aktarımı sonrası mesaj: "X fiş aktarıldı" + (varsa) ", Y satır Aktarım Hataları'na düştü". */
export const excelAktarimMesaji = (aktarilan: number, hataEkraninda: number | null | undefined): string => {
    let m = `${aktarilan} fiş aktarıldı`;
    if (hataEkraninda && hataEkraninda > 0)
        m += `, ${hataEkraninda} satır Aktarım Hataları'na düştü`;
    return m + '.';
}

/**
 * Son çekim zamanı: ISO ("2026-11-04T10:30:00") gelirse "04.11.2026 10:30" yazılır; sunucu zaten biçimlenmiş metin
 * gönderirse olduğu gibi kalır. Boşsa "Henüz çekim yapılmadı".
 */
export const sonCalismaYaz = (s: string | null | undefined): string => {
    if (!s || s.trim() == '')
        return 'Henüz çekim yapılmadı';

    const m = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/.exec(s);
    if (!m)
        return s;

    // Saat dilimi eki (Z / +03:00) varsa yerel saate çevrilir; yoksa yazıldığı gibi.
    if (/(Z|[+-]\d{2}:?\d{2})$/.test(s)) {
        const d = new Date(s);
        if (!isNaN(d.getTime()))
            return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    }

    return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
}

const sade = (s: string) => s.toLocaleUpperCase('tr-TR').replace(/[^0-9A-ZÇĞİÖŞÜ]/g, '');

/**
 * Düzelt penceresinde yakıt türü ön seçimi: hata satırındaki ürün adı (ör. "MOTORİN", "Kurşunsuz Benzin 95") yakıt türü
 * seçeneklerinden biriyle eşleşirse o seçenek. Önce büyük/küçük harf ve boşluk duyarsız tam eşleşme, sonra birinin
 * diğerini içermesi (en uzun ad kazanır). Eşleşme yoksa undefined (alan boş kalır; isteğe bağlı).
 */
export const yakitTuruEslestir = <T extends { label: string }>(urun: string | null | undefined, secenekler: T[]): T | undefined => {
    if (!urun || urun.trim() == '')
        return undefined;

    const u = sade(urun);
    if (u == '')
        return undefined;

    const tam = secenekler.find(x => sade(x.label) == u);
    if (tam)
        return tam;

    const icerenler = secenekler.filter(x => { const l = sade(x.label); return l != '' && (u.includes(l) || l.includes(u)); });
    return icerenler.sort((a, b) => sade(b.label).length - sade(a.label).length)[0];
}

/** Rozet sayısı: 99'dan büyükse "99+". */
export const rozetSayisi = (n: number): string => (n > 99 ? '99+' : String(n));
